import type {Size2Like, Vector2Like} from "@cm/math"
import type {HalImageDescriptor} from "@cm/hal"
import {getMipLevelSize} from "@cm/hal"
import {WebGl2ImageAtlas} from "#webgl2/webgl2-image-atlas"
import {getNumChannels} from "@cm/hal/hal-image/utils"

export class WebGl2ImageVirtualMipmapInfo {
    constructor(descriptor: HalImageDescriptor) {
        this.numBaseLevelPages = {
            x: Math.ceil(descriptor.width / WebGl2ImageAtlas.pageSize),
            y: Math.ceil(descriptor.height / WebGl2ImageAtlas.pageSize),
        }
        this.numChannels = getNumChannels(descriptor.channelLayout)
        let currPageIndex = 0
        for (let mipLevel = 0; mipLevel < descriptor.numMipLevels; mipLevel++) {
            const mipLevelSize = getMipLevelSize(descriptor, mipLevel)
            const numMipLevelPages = {
                x: Math.ceil(mipLevelSize.width / WebGl2ImageAtlas.pageSize),
                y: Math.ceil(mipLevelSize.height / WebGl2ImageAtlas.pageSize),
            }
            this.level.push({
                firstPageIndex: currPageIndex,
                mipLevelSize,
                numMipLevelPages,
            })
            const numTotalMipLevelPages = numMipLevelPages.x * numMipLevelPages.y
            currPageIndex += numTotalMipLevelPages
        }
        const numPages = (currPageIndex + 1) * this.numChannels
        const numPagesSqrt = Math.ceil(Math.sqrt(numPages))
        this.pageLookupSize = {
            width: numPagesSqrt,
            height: numPagesSqrt,
        }
    }

    // returns the number of mip levels
    getNumMipLevels(): number {
        return this.level.length
    }

    // returns the mip level size for the given mip level
    getMipLevelSize(mipLevel: number): Size2Like {
        if (mipLevel < 0 || mipLevel >= this.level.length || !Number.isInteger(mipLevel)) {
            throw new Error(`Invalid mip level: ${mipLevel}`)
        }
        return this.level[mipLevel].mipLevelSize
    }

    // returns the linearized page index (across all mip levels)
    getLinearPageIndex(args: {
        mipLevel: number
        pageIndex: Vector2Like // 2D page index on the mip level
    }): number {
        if (args.mipLevel < 0 || args.mipLevel >= this.level.length || !Number.isInteger(args.mipLevel)) {
            throw new Error(`Invalid mip level: ${args.mipLevel}`)
        }
        const level = this.level[args.mipLevel]
        if (args.pageIndex.x < 0 || args.pageIndex.x >= level.numMipLevelPages.x || args.pageIndex.y < 0 || args.pageIndex.y >= level.numMipLevelPages.y) {
            throw new Error(`Invalid page index: ${args.pageIndex.x}, ${args.pageIndex.y}`)
        }
        const relativePageIndex = args.pageIndex.y * level.numMipLevelPages.x + args.pageIndex.x
        return level.firstPageIndex + relativePageIndex
    }

    // returns the number of pages in the given mip level (as 2D count)
    getNumMipLevelPages(mipLevel: number): Vector2Like {
        if (mipLevel < 0 || mipLevel >= this.level.length || !Number.isInteger(mipLevel)) {
            throw new Error(`Invalid mip level: ${mipLevel}`)
        }
        return this.level[mipLevel].numMipLevelPages
    }

    // returns the 2D texel index in the page lookup texture (used for sampling the page table texture)
    getPageLookupTexel(linearPageIndex: number, channel: number) {
        linearPageIndex = linearPageIndex * this.numChannels + channel
        const x = linearPageIndex % this.pageLookupSize.width
        const y = Math.floor(linearPageIndex / this.pageLookupSize.width)
        return {x, y}
    }

    // returns the pixel offset within the page (used for packing multiple mip levels into the same page)
    // getPagePixelOffset(mipLevel: number): Vector2Like {
    //     if (mipLevel < 0 || mipLevel >= this.level.length || !Number.isInteger(mipLevel)) {
    //         throw new Error(`Invalid mip level: ${mipLevel}`)
    //     }
    //     const level = this.level[mipLevel]
    //     return level.pagePixelOffset
    // }

    readonly numChannels: number
    readonly numBaseLevelPages: Vector2Like
    readonly pageLookupSize: Size2Like
    readonly level: MipLevelInfo[] = []
}

type MipLevelInfo = {
    firstPageIndex: number
    mipLevelSize: Size2Like
    numMipLevelPages: Vector2Like
}
